import { useContext, useEffect, useMemo, useRef, useState } from "react";
import TablaCliente from "./TablaCliente";
import { useTableColumnPreference } from "../../../../../hooks/useTableColumnPreference";
import { ColumnVisibilityProvider } from "../../../../../context/ColumnVisibilityContext";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ActionMassive from "./ActionMassive";
import SeleccionContext from "../../../../../context/Seleccion/SeleccionContext";
import IconLoading from "../../../../ui/kit-svg/IconLoading";
import TableSkeleton from "../../../../ui/kit-mdtw/table/TableSkeleton";
import FilterChips from "../../../../ui/FilterChips";
import FooterTablePagination from "../../../../ui/kit-mdtw/table/FooterTablePagination";

// Columnas alternables (selección y acciones son fijas). Coincide con las claves
// válidas del backend (SELECCION_MASIVA_VALIDADOS). El encabezado y las celdas se
// dibujan en componentes anidados (ItemProceso / TablaCandidato), que leen la
// visibilidad vía ColumnVisibilityContext.
const COLUMN_DEFINITIONS = [
  { key: "estado", label: "Estado Registro" },
  { key: "validacion", label: "Validación" },
  { key: "nombre", label: "Nombre" },
  { key: "rut", label: "Rut" },
  { key: "celular", label: "Celular" },
  { key: "fechaValidacion", label: "Fecha Validación" },
  { key: "reclutador", label: "Reclutador" },
  { key: "confirmaEntrevista", label: "Confirma Entrevista" },
] as const;

const ListadoCandidatoValidado = () => {
  const {
    toasts,
    listaCandidatoSeleccionado,
    getCandidatoSeleccionado,
    putAvanzarProceso,
    getMetodoMovilizacion,
    postSolicitarCandidato,
    token,
    userId,
  } = useContext(SeleccionContext);
  const [isLoadingTable, setIsLoadingTable] = useState<boolean | undefined>(
    undefined
  );
  const [isErrorTable, setIsErrorTable] = useState<boolean | undefined>(
    undefined
  );
  const [listadoSeleccionado, setListadoSeleccionado] = useState<string[]>([]);
  const [currentProcesos, setCurrentProcesos] = useState<string[]>([]);
  const [localStorageSelect, setLocalStorageSelect] = useState<string>(
    "selectCandidatoValidadoMasivo"
  );
  const [localStorageFilter, setLocalStorageFilter] = useState<string>(
    "filtroCandidatoValidadoMasivo"
  );
  const [candidates, setCandidates] = useState<any>([]);
  const [solicitudes, setSolicitudes] = useState<any>([]);
  const [selectedProcesoDestino, setSelectedProcesoDestino] = useState<string | undefined>(undefined);
  const [selectedComentario, setSelectedComentario] = useState<string>("");
  const [offset, setOffset] = useState<any>({
    offset: { value: undefined, valid: true },
    pages: 1,
  });
  const [paginationData, setPaginationData] = useState<any>({});
  const [searchValue, setSearchValue] = useState<string>("");
  const searchRef = useRef<string>("");
  const [formFilter, setFormFilter] = useState<any>({
    estado: [],
    nombre: [],
    rut: [],
    proceso: [],
    cliente: [],
    tipoProceso: ["MASIVO"],
    fechaValidacion: [],
    fechaPreseleccion: [],
    confirmaEntrevista: [],
    transporteTeamwork: [],
    entrevistaRealizada: [],
    reclutador: [],
  });

  // Preferencia de columnas visibles (misma lógica que Postulaciones).
  const loggedUserId = useMemo(() => sessionStorage.getItem("usuarioId") ?? "", []);
  const {
    visibleColumnKeys,
    setVisibleColumnKeys,
    isSaving: isSavingColumns,
    isSynchronized: isColumnsSynchronized,
  } = useTableColumnPreference({
    table: "SELECCION_MASIVA_VALIDADOS",
    storageNamespace: "seleccion-masiva-validados",
    columnDefinitions: COLUMN_DEFINITIONS,
    loggedUserId,
  });
  const visibleColCount = 2 + visibleColumnKeys.length; // checkbox + acciones fijas

  const handleSearch = (term: string): void => {
    searchRef.current = term;
    setSearchValue(term);
    setOffset((prev: any) => ({ ...prev, pages: 1 }));
  };

  useEffect(() => {
    listRefresh();
  }, [token, localStorage.getItem(localStorageFilter), offset.pages, offset.offset, searchValue]);

  useEffect(() => {
    // Al entrar a la pestaña se parte siempre sin selección: se limpia
    // cualquier selección persistida de una sesión/pestaña anterior. Antes se
    // restauraba desde localStorage y eso dejaba el contador "1 ítem
    // seleccionado" al abrir (fila fantasma en Validados / fila marcada por
    // defecto en Seleccionados) sin que el usuario hubiese seleccionado nada.
    localStorage.removeItem(localStorageSelect);
    setListadoSeleccionado([]);
  }, []);

  useEffect(() => {
    if (listaCandidatoSeleccionado && listaCandidatoSeleccionado.length > 0) {
      const listProcesos = [...listaCandidatoSeleccionado];
      const arrayProcesos: string[] = listProcesos
        .map(({ grupos }: any) => grupos)
        .flat()
        .map(({ procesos }: any) => procesos)
        .flat()
        .map(({ personas }: any) => personas)
        .flat()
        .map(({ personaDNI }: any) => personaDNI);
      setCurrentProcesos(arrayProcesos);
    }
  }, [listaCandidatoSeleccionado]);

  const listRefresh = () => {
    if (token) {
      getMetodoMovilizacion({ token });
      const busPerIdList = (listaCandidatoSeleccionado || [])
        .flatMap(({ grupos }: any) => grupos || [])
        .flatMap(({ procesos }: any) => procesos || [])
        .flatMap(({ personas }: any) => personas || [])
        .filter(({ personaDNI }: any) => listadoSeleccionado.includes(personaDNI))
        .map(({ busPerId }: any) => busPerId)
        .filter(Boolean);
      if (localStorage.getItem(localStorageFilter)) {
        const copyStorage: any = localStorage.getItem(localStorageFilter);
        const copyStorageChange = { ...JSON.parse(copyStorage), busPerIdList };
        copyStorageChange.tipoProceso = ["MASIVO"];
        getCandidatoSeleccionado({
          token,
          filtros: { ...copyStorageChange, search: searchRef.current },
          setIsErrorTable,
          setIsLoadingTable,
          setPaginationData,
          page: offset.pages ?? 1,
          limit: offset.offset.value?.code ?? 10,
        });
      } else {
        getCandidatoSeleccionado({
          token,
          filtros: { ...formFilter, busPerIdList, search: searchRef.current },
          setIsErrorTable,
          setIsLoadingTable,
          setPaginationData,
          page: offset.pages ?? 1,
          limit: offset.offset.value?.code ?? 10,
        });
      }
    }
  };

  const handleClickAddAll = (): void => {
    const arrayListado: string[] = [...listadoSeleccionado];
    if (listadoSeleccionado.length < currentProcesos.length) {
      currentProcesos
        .filter(
          (busquedaId: string) => !listadoSeleccionado.includes(busquedaId)
        )
        .map((busquedaId: string) => arrayListado.push(busquedaId));
    } else {
      arrayListado.splice(0, currentProcesos.length);
    }
    localStorage.setItem(localStorageSelect, String(arrayListado));
    setListadoSeleccionado(arrayListado);
  };

  const handleAvanzarProceso = async (values: any) => {
    const { setIsOpenModalNoProcesado, localStorageSelect, estadoDestino } = values;
    if (token) {
      await putAvanzarProceso({
        token,
        procesos: candidates,
        setIsOpenModalNoProcesado,
        localStorageSelect,
        estadoDestino,
      });
      listRefresh();
    }
  };

  const handleClickSolicitarCandidato = async (args?: { procesoDestino?: string; comentario?: string }): Promise<void> => {
    const procesoId = args?.procesoDestino ?? selectedProcesoDestino;
    const comentario = args?.comentario ?? selectedComentario ?? "";
    if (token && procesoId) {
      const allPersonas = (listaCandidatoSeleccionado || [])
        .flatMap(({ grupos }: any) => grupos || [])
        .flatMap(({ procesos }: any) => procesos || [])
        .flatMap(({ personas }: any) => personas || []);
      await postSolicitarCandidato({
        token,
        solicitudes: listadoSeleccionado.map((dni: string) => {
          const candidate = allPersonas.find((c: any) => c.personaDNI === dni);
          return { personaId: candidate?.personaId ?? dni, procesoId, comentario };
        }),
      });
    }
  };

  return (
    <>
      {toasts}
      <section className="content-seleccion-multiple">
        <div className="content-multiple">
          <button onClick={handleClickAddAll} className="button-multiple">
            {listadoSeleccionado.length == currentProcesos.length &&
            listadoSeleccionado.length > 0 ? (
              <CheckBoxIcon />
            ) : (
              <CheckBoxOutlineBlankIcon />
            )}
          </button>
          <label className="title-multiple">
            Seleccionar Todo{" "}
            <span
              className={`selects-badge${listadoSeleccionado.length === 0 ? " inactive" : ""}`}
              style={{ transition: "background 0.3s ease, color 0.3s ease, font-weight 0.3s ease" }}
            >
              {listadoSeleccionado.length > 0
                ? `${listadoSeleccionado.length} ítem${listadoSeleccionado.length !== 1 ? "s" : ""} seleccionado${listadoSeleccionado.length !== 1 ? "s" : ""}`
                : "0 ítems seleccionados"}
            </span>
          </label>
        </div>
        <FilterChips localStorageFilter={localStorageFilter} setFormFilter={setFormFilter} />
      </section>
      <section
        className="content-seleccion-multiple action-in-line margin-top-20"
        style={{ flexDirection: "column", alignItems: "stretch" }}
      >
        <ActionMassive
          localStorageFilter={localStorageFilter}
          localStorageSelect={localStorageSelect}
          listadoSeleccionado={listadoSeleccionado}
          formFilter={formFilter}
          setFormFilter={setFormFilter}
          handleAvanzarProceso={handleAvanzarProceso}
          candidates={candidates}
          setCandidates={setCandidates}
          listRefresh={listRefresh}
          searchValue={searchValue}
          onSearch={handleSearch}
          setListadoSeleccionado={setListadoSeleccionado}
          solicitudes={solicitudes}
          setSolicitudes={setSolicitudes}
          handleClickSolicitarCandidato={handleClickSolicitarCandidato}
          columnDefinitions={COLUMN_DEFINITIONS}
          visibleColumnKeys={visibleColumnKeys}
          setVisibleColumnKeys={(keys) => setVisibleColumnKeys(keys as any)}
          isSavingColumns={isSavingColumns}
          isColumnsSynchronized={isColumnsSynchronized}
        />
      </section>

      {!isLoadingTable ? (
        <>
          {isErrorTable ? (
            <div className="container-is-error-table">
              Error al cargar los datos.
            </div>
          ) : (
            <ColumnVisibilityProvider visibleColumns={visibleColumnKeys}>
              <TablaCliente
                localStorageSelect={localStorageSelect}
                localStorageFilter={localStorageFilter}
                listadoSeleccionado={listadoSeleccionado}
                setListadoSeleccionado={setListadoSeleccionado}
                formFilter={formFilter}
                listRefresh={listRefresh}
              />
            </ColumnVisibilityProvider>
          )}
        </>
      ) : (
        <TableSkeleton cols={visibleColCount} />
      )}
      {paginationData?.totalPages > 1 && (
        <FooterTablePagination
          paginationData={paginationData}
          offset={offset}
          setOffset={setOffset}
        />
      )}
    </>
  );
};

export default ListadoCandidatoValidado;
